import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, CreditCard, Info, Trash2 } from 'lucide-react';
import { SPRING } from './motion';
import './ConfirmDialog.css';

/* Detail rows used by payment / scheduled-change confirms — render as
 * a label / value grid inside a soft callout so the user sees the
 * "what's about to happen" data at a glance (current plan, target
 * plan, charge today, renewal date, etc.). */
export interface ConfirmDetail {
  label: string;
  value: React.ReactNode;
  /** Highlight the value with the variant's accent colour. */
  emphasize?: boolean;
}

export type ConfirmVariant = 'danger' | 'warning' | 'info' | 'payment';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  /** Optional. Plain string OR JSX (for rich content). Both flow into
   *  the same <div>, so wrap multi-line content in your own elements. */
  message?: React.ReactNode;
  /** Optional structured rows ("Current → New", "Charge today", etc).
   *  Rendered in a soft callout under the message. */
  details?: ConfirmDetail[];
  /** Optional bulleted what-happens-next list. */
  consequences?: React.ReactNode[];
  confirmText?: string;
  cancelText?: string;
  /** Hides the cancel button — info-only acknowledge dialog. Rare. */
  hideCancel?: boolean;
  /** Disable the confirm button and show a busy label (caller-driven). */
  busy?: boolean;
  busyText?: string;
  /** Disable confirm WITHOUT the busy label — for a dialog whose body holds a
   *  required field. Distinct from `busy`: nothing is in flight, the input is
   *  simply not yet valid, so the label must stay the real verb. */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: ConfirmVariant;
}

/* CC8: lucide icons in a tinted tile. danger = red (destructive), warning =
 * amber, info and payment = teal. */
const VARIANT_ICONS: Record<ConfirmVariant, React.ReactNode> = {
  danger: <Trash2 aria-hidden="true" />,
  warning: <AlertTriangle aria-hidden="true" />,
  info: <Info aria-hidden="true" />,
  payment: <CreditCard aria-hidden="true" />,
};

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  details,
  consequences,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  hideCancel = false,
  busy = false,
  busyText,
  confirmDisabled = false,
  onConfirm,
  onCancel,
  variant = 'warning',
}) => {
  // Focus the primary action on open so Enter confirms — EXCEPT for
  // destructive variants where an accidental Enter could nuke data.
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    if (variant === 'danger') return;
    // Tiny delay so framer-motion's enter animation doesn't steal focus.
    const t = setTimeout(() => confirmRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [isOpen, variant]);

  // ESC = cancel. Disabled while busy so a misfire mid-payment doesn't
  // close the dialog with the request still in flight.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, busy, onCancel]);

  // Lock background scroll while up. Without this, mobile users can
  // scroll behind the backdrop which feels broken.
  useEffect(() => {
    if (!isOpen) return;
    const prior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prior;
    };
  }, [isOpen]);

  // PP-03: the backdrop blur is applied after the card settles (CSS
  // .is-settled) so the full-viewport filter never runs during the spring.
  const [settled, setSettled] = React.useState(false);
  React.useEffect(() => {
    if (!isOpen) setSettled(false);
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className={`confirm-dialog-backdrop${settled ? ' is-settled' : ''}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.16 } }}
            transition={{ duration: 0.22 }}
            onClick={busy ? undefined : onCancel}
          />
          <motion.div
            className="confirm-dialog-container"
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 8, transition: { duration: 0.14 } }}
            transition={SPRING}
            onAnimationComplete={() => setSettled(true)}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="confirm-dialog-title"
              className={`confirm-dialog confirm-dialog-${variant}`}
            >
              <div className="confirm-dialog-icon" aria-hidden="true">
                {VARIANT_ICONS[variant]}
              </div>
              <h3 id="confirm-dialog-title" className="confirm-dialog-title">
                {title}
              </h3>
              {message && <div className="confirm-dialog-message">{message}</div>}

              {details && details.length > 0 && (
                <div className="confirm-dialog-details">
                  {details.map((row, i) => (
                    <div key={i} className="confirm-dialog-detail-row">
                      <span className="confirm-dialog-detail-label">{row.label}</span>
                      <strong
                        className={`confirm-dialog-detail-value${row.emphasize ? ' confirm-dialog-detail-value-emphasis' : ''}`}
                      >
                        {row.value}
                      </strong>
                    </div>
                  ))}
                </div>
              )}

              {consequences && consequences.length > 0 && (
                <ul className="confirm-dialog-consequences">
                  {consequences.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              )}

              <div className="confirm-dialog-actions">
                {!hideCancel && (
                  <button
                    type="button"
                    className="lnr-btn lnr-btn--secondary lnr-btn--lg confirm-dialog-btn confirm-dialog-btn-cancel"
                    onClick={onCancel}
                    disabled={busy}
                  >
                    {cancelText}
                  </button>
                )}
                <button
                  ref={confirmRef}
                  type="button"
                  className={`lnr-btn ${variant === 'danger' ? 'lnr-btn--destructive' : 'lnr-btn--primary'} lnr-btn--lg confirm-dialog-btn confirm-dialog-btn-confirm confirm-dialog-btn-${variant}`}
                  onClick={onConfirm}
                  disabled={busy || confirmDisabled}
                  style={
                    busy
                      ? { cursor: 'wait', opacity: 0.78 }
                      : confirmDisabled
                        ? { cursor: 'not-allowed', opacity: 0.55 }
                        : undefined
                  }
                >
                  {busy ? busyText || 'Working…' : confirmText}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default ConfirmDialog;
